import { getVisibleError, handleWidgetUpdate, renderForm, validateCustom } from '@juvare/common-form-ui/WidgetForm/Utils';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { showConfirmDialog, showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import AddIcon from '@mui/icons-material/Add';
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../hooks';
import { useAlertServiceClient } from '../../hooks';
import { downloadBlob } from '../../utils';
import { VoiceRecording } from './Components/VoiceRecording';
import { VoiceRecordingDialog } from './Components/VoiceRecordingDialog';
import { type } from './types';
// There is an issue in the widget that comes from react-media-recorder library, which causes
// app to throw "There is already an encoder stored which handles exactly the same mime types" error
// This is caused by unhandled code inside the library, and only throws errors in strict mode (local development env)
const WidgetRecordMessage = (props) => {
    var _a, _b, _c;
    const { t } = useAlertUiTranslation();
    const voiceRecordings = props.getData();
    const alertServiceClient = useAlertServiceClient();
    const error = getVisibleError(props);
    const [recordingBlobs, setRecordingBlobs] = React.useState({
        message: null,
        responses: null,
        instructions: null,
    });
    const updateRecordingBlobsForType = (type) => (newBlob) => {
        setRecordingBlobs((prevBlobs) => (Object.assign(Object.assign({}, prevBlobs), { [type]: newBlob })));
    };
    const addVoiceRecording = (newRecording) => {
        var _a;
        const filteredRecordings = (_a = voiceRecordings === null || voiceRecordings === void 0 ? void 0 : voiceRecordings.filter((recording) => recording.type !== newRecording.type)) !== null && _a !== void 0 ? _a : [];
        props.setData([...filteredRecordings, newRecording]);
        renderForm(props, { changed: true });
    };
    const downloadRecording = (recording) => {
        if (recording.id) {
            alertServiceClient
                .getDocument(recording.id)
                .then((blob) => downloadBlob(blob, recording.type + '.mp3'))
                .catch(handleErrorSnackbar);
        }
    };
    const fetchExistingRecording = (recording) => {
        if (recording.id) {
            alertServiceClient.getDocument(recording.id).then((blob) => {
                const isRecordingStillRelevant = voiceRecordings === null || voiceRecordings === void 0 ? void 0 : voiceRecordings.some((rec) => rec.id === recording.id);
                if (isRecordingStillRelevant) {
                    setRecordingBlobs((prevBlobs) => (Object.assign(Object.assign({}, prevBlobs), { [recording.type]: blob })));
                }
            });
        }
    };
    const removeRecording = (recording) => {
        const updatedRecordings = (voiceRecordings === null || voiceRecordings === void 0 ? void 0 : voiceRecordings.filter((r) => r.type !== recording.type)) || [];
        props.setData(updatedRecordings);
        renderForm(props, { changed: true });
    };
    const handleRemoveRecording = (recording) => {
        showConfirmDialog({
            title: t('pages.edit.voice.record.deleteRecording'),
            content: React.createElement(Typography, null, t('pages.edit.voice.record.deleteRecordingDialogContent')),
            confirmLabel: t('pages.edit.voice.record.deleteRecordingDialogContinue'),
            onConfirm: () => removeRecording && removeRecording(recording),
        });
    };
    const updateRecording = (recording) => {
        showConfirmDialog({
            title: t('pages.edit.voice.record.title'),
            content: React.createElement(Typography, null, t('pages.edit.voice.record.recordSegmentDialogContent')),
            confirmLabel: t('pages.edit.voice.record.recordSegmentDialogContinue'),
            onConfirm: () => {
                showCustomDialog({
                    content: (close) => (React.createElement(VoiceRecordingDialog, { close: close, type: recording.type, onSave: addVoiceRecording, responseOptions: props.rootData.responseOptions, message: props.rootData.voiceMessage })),
                });
                setRecordingBlobs((prevBlobs) => (Object.assign(Object.assign({}, prevBlobs), { [recording.type]: null })));
            },
        });
    };
    return (React.createElement(Box, { sx: { display: 'inline-grid', justifyItems: 'left', pl: 2, width: '100%' } },
        (voiceRecordings === null || voiceRecordings === void 0 ? void 0 : voiceRecordings.some((rec) => rec.type === 'message')) ? (voiceRecordings
            .filter((rec) => rec.type === 'message')
            .map((recording) => {
            var _a;
            return (React.createElement(Box, { key: recording.id, sx: { width: '96%', ml: -2 } },
                React.createElement(VoiceRecording, { key: recording.id, downloadRecording: downloadRecording, fetchExistingRecording: fetchExistingRecording, voiceRecording: recording, disabled: (_a = props.config) === null || _a === void 0 ? void 0 : _a.disabled, setRecordingBlob: updateRecordingBlobsForType(recording.type), recordingBlob: recordingBlobs[recording.type], removeRecording: handleRemoveRecording, updateRecording: updateRecording })));
        })) : (React.createElement(React.Fragment, null,
            React.createElement(Button, { startIcon: React.createElement(AddIcon, null), variant: 'text', "data-testid": 'record-message-button', color: error ? 'error' : 'primary', disabled: (_a = props.config) === null || _a === void 0 ? void 0 : _a.disabled, onClick: () => {
                    showCustomDialog({
                        content: (close) => (React.createElement(VoiceRecordingDialog, { close: close, type: 'message', onSave: addVoiceRecording, responseOptions: props.rootData.responseOptions, message: props.rootData.voiceMessage })),
                    });
                } }, t('pages.edit.voice.record.recordMessage')),
            error && (React.createElement(Typography, { variant: 'body2', sx: { pl: 1, color: themeColors.accent.red.bold } }, error)))),
        (voiceRecordings === null || voiceRecordings === void 0 ? void 0 : voiceRecordings.some((rec) => rec.type === 'responses')) ? (voiceRecordings
            .filter((rec) => rec.type === 'responses')
            .map((recording) => {
            var _a;
            return (React.createElement(Box, { key: recording.id, sx: { width: '96%', my: 2, ml: -2 } },
                React.createElement(VoiceRecording, { key: recording.id, downloadRecording: downloadRecording, fetchExistingRecording: fetchExistingRecording, voiceRecording: recording, disabled: (_a = props.config) === null || _a === void 0 ? void 0 : _a.disabled, setRecordingBlob: updateRecordingBlobsForType(recording.type), recordingBlob: recordingBlobs[recording.type], removeRecording: handleRemoveRecording, updateRecording: updateRecording })));
        })) : (React.createElement(Button, { startIcon: React.createElement(AddIcon, null), variant: 'text', "data-testid": 'record-response-button', sx: { my: 2 }, disabled: (_b = props.config) === null || _b === void 0 ? void 0 : _b.disabled, onClick: () => {
                showCustomDialog({
                    content: (close) => (React.createElement(VoiceRecordingDialog, { close: close, type: 'responses', onSave: addVoiceRecording, responseOptions: props.rootData.responseOptions, message: props.rootData.voiceMessage })),
                });
            } }, t('pages.edit.voice.record.recordResponse'))),
        (voiceRecordings === null || voiceRecordings === void 0 ? void 0 : voiceRecordings.some((rec) => rec.type === 'instructions')) ? (voiceRecordings
            .filter((rec) => rec.type === 'instructions')
            .map((recording) => {
            var _a;
            return (React.createElement(Box, { key: recording.id, sx: { width: '96%', ml: -2 } },
                React.createElement(VoiceRecording, { key: recording.id, downloadRecording: downloadRecording, fetchExistingRecording: fetchExistingRecording, voiceRecording: recording, disabled: (_a = props.config) === null || _a === void 0 ? void 0 : _a.disabled, setRecordingBlob: updateRecordingBlobsForType(recording.type), recordingBlob: recordingBlobs[recording.type], removeRecording: handleRemoveRecording, updateRecording: updateRecording })));
        })) : (React.createElement(Button, { startIcon: React.createElement(AddIcon, null), variant: 'text', "data-testid": 'record-instruction-button', disabled: (_c = props.config) === null || _c === void 0 ? void 0 : _c.disabled, onClick: () => {
                showCustomDialog({
                    content: (close) => (React.createElement(VoiceRecordingDialog, { close: close, type: 'instructions', onSave: addVoiceRecording, responseOptions: props.rootData.responseOptions, message: props.rootData.voiceMessage })),
                });
            } }, t('pages.edit.voice.record.recordInstruction')))));
};
export const WidgetRecordMessageEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props),
        React.createElement(WidgetRecordMessage, Object.assign({}, props))));
};
export const widgetRecordMessage = {
    type,
    createWidget: () => ({
        type,
        label: 'Record Message',
        key: 'voiceRecordings',
    }),
    validate: (props) => {
        var _a;
        const value = props.getData();
        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) &&
            props.rootData.voiceMessageType === 'record' &&
            (!(value === null || value === void 0 ? void 0 : value.length) || !value.some((recording) => recording.type === 'message'))) {
            return 'Message cannot be empty.';
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        const handler = new WidgetHandler(props);
        handler.config.existingVoiceRecordings = handler.formData.voiceRecordings;
        const prevData = handler.config.existingVoiceRecordings;
        if (prevData !== undefined && prevData.length > 0) {
            handler.formData.voiceRecordings = prevData;
        }
        else {
            handler.formData.voiceRecordings = [];
        }
        return handleWidgetUpdate(props);
    },
    icon: React.createElement(KeyboardVoiceOutlinedIcon, null),
    editorComponent: WidgetRecordMessageEditor,
    component: WidgetRecordMessage,
};
